/**
 * Market picker. A dropdown that hangs from whatever opened it: the market name on the trade screen, the top bar
 * search, or (for "/" and Ctrl/⌘ K) the top bar search when it is on screen. Styled like the account menu; phones get
 * the same panel spanning the screen. One compact row per market: pair, name, leverage, price, 24h change.
 */
import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { fmtPct, fmtPrice, instrumentTag, searchInstruments, type AssetClass, type Instrument, type Ticker } from '@platform/shared';
import { useMedia } from '../lib/media';
import { Link, match, navigate, usePath } from '../lib/router';
import { useInstruments, useTickers } from '../stores/market';
import { addRecent, clearRecent, closeSearch, openSearch, useRecentMarkets, useSearchAnchor, useSearchOpen } from '../stores/search';
import { InstrumentIcon } from './InstrumentIcon';
import { Marked, PairName } from './PairName';
import { cx, Icon, MoveArrow } from './ui';

type Filter = AssetClass | 'ALL';

const FILTERS: { id: Filter; label: string }[] = [
  { id: 'ALL', label: 'All' },
  { id: 'CRYPTO', label: 'Crypto' },
  { id: 'STOCK', label: 'Stocks' },
  { id: 'COMMODITY', label: 'Commodities' },
  { id: 'INDEX', label: 'Indices' },
  { id: 'FX', label: 'Forex' },
];

/** How many markets Trending lists before anything is typed. */
const TRENDING = 10;
/** Panel width from `sm` up; below it the panel spans the screen less an 8px margin each side. */
const WIDTH = 460;
const GAP = 6;
const EDGE = 8;

/** Icon, market, leverage, price, 24h: shared by the column heads and every row so they line up. Phones drop leverage. */
const COLS = 'grid grid-cols-[16px_minmax(0,1fr)_40px_88px_60px] items-center gap-2 max-sm:grid-cols-[16px_minmax(0,1fr)_84px_56px]';

const vol = (t: Ticker | undefined) => (t ? Number(t.vol24) || 0 : 0);

interface Row {
  inst: Instrument;
  via: string | null;
}

/** Opens the picker on "/" or Ctrl/⌘ K, and renders it while open. Mounted once, in the app shell. */
export function MarketSearch() {
  const open = useSearchOpen();
  const narrow = !useMedia('(min-width: 640px)');
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      const typing = ['INPUT', 'TEXTAREA', 'SELECT'].includes(t.tagName) || t.isContentEditable;
      if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        openSearch();
      } else if (e.key === '/' && !typing && !e.metaKey && !e.ctrlKey && !e.altKey) {
        e.preventDefault();
        openSearch();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return open ? <SearchPanel narrow={narrow} /> : null;
}

/** The top bar's search button, when it is on screen: where the keyboard shortcuts open the picker from. */
function fallbackAnchor(): HTMLElement | null {
  const el = document.querySelector<HTMLElement>('[data-search-anchor]');
  return el && el.getClientRects().length ? el : null;
}

/** Fixed position under `anchor`, kept inside the viewport (and above the phone keyboard). */
function placement(anchor: HTMLElement | null, narrow: boolean): CSSProperties {
  const vw = window.innerWidth;
  const vh = window.visualViewport?.height ?? window.innerHeight;
  const r = (anchor?.isConnected ? anchor : fallbackAnchor())?.getBoundingClientRect();
  const top = r ? r.bottom + GAP : 64;
  const maxHeight = Math.max(240, Math.min(narrow ? vh : 600, vh - top - EDGE));
  if (narrow) return { top, left: EDGE, right: EDGE, maxHeight };
  const width = Math.min(WIDTH, vw - 2 * EDGE);
  // Hang from the anchor's left edge, or its right edge when it sits on the right of the screen.
  const want = !r ? (vw - width) / 2 : r.left + r.width / 2 > vw / 2 ? r.right + GAP - width : r.left - GAP;
  return { top, left: Math.min(Math.max(want, EDGE), vw - width - EDGE), width, maxHeight };
}

/** placement(), measured before the first paint (so the search field can take focus) and kept up to date. */
function usePlacement(anchor: HTMLElement | null, narrow: boolean): CSSProperties {
  const [style, setStyle] = useState(() => placement(anchor, narrow));
  useLayoutEffect(() => {
    const place = () => setStyle(placement(anchor, narrow));
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    window.visualViewport?.addEventListener('resize', place);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
      window.visualViewport?.removeEventListener('resize', place);
    };
  }, [anchor, narrow]);
  return style;
}

function SearchPanel({ narrow }: { narrow: boolean }) {
  const instruments = useInstruments();
  const tickers = useTickers();
  const recent = useRecentMarkets();
  const anchor = useSearchAnchor();
  const path = usePath();
  const current = match('/trade/:symbol', path)?.symbol;
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('ALL');
  // -1 until the keyboard or pointer moves it: the current market, else the first row.
  const [sel, setSel] = useState(-1);
  const panel = useRef<HTMLDivElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const style = usePlacement(anchor, narrow);

  useEffect(() => {
    input.current?.focus();
    // Close on a press outside the panel. Presses on the anchor are left to its own click, which toggles.
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!panel.current?.contains(t) && !(anchor ?? fallbackAnchor())?.contains(t)) closeSearch();
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [anchor]);

  // A route change (a pick, "All markets", the back button) closes it.
  const firstPath = useRef(path);
  useEffect(() => {
    if (path !== firstPath.current) closeSearch();
  }, [path]);

  const hits = useMemo(() => searchInstruments(instruments, query, (i) => vol(tickers[i.symbol])), [instruments, query, tickers]);
  const counts = useMemo(() => {
    const c: Partial<Record<Filter, number>> = { ALL: hits.length };
    for (const h of hits) c[h.item.assetClass] = (c[h.item.assetClass] ?? 0) + 1;
    return c;
  }, [hits]);
  const typed = query.trim() !== '';
  const rows: Row[] = useMemo(() => {
    // A match on the market code needs no note: the code is on the row, highlighted.
    if (typed) return hits.filter((h) => filter === 'ALL' || h.item.assetClass === filter).map((h) => ({ inst: h.item, via: h.via === instrumentTag(h.item.symbol) ? null : h.via }));
    return instruments
      .filter((i) => filter === 'ALL' || i.assetClass === filter)
      .sort((a, b) => vol(tickers[b.symbol]) - vol(tickers[a.symbol]))
      .slice(0, TRENDING)
      .map((inst) => ({ inst, via: null }));
  }, [typed, hits, filter, instruments, tickers]);
  const recentInst = useMemo(() => recent.map((s) => instruments.find((i) => i.symbol === s)).filter((i): i is Instrument => !!i), [recent, instruments]);
  const active = sel >= 0 ? sel : Math.max(0, rows.findIndex((r) => r.inst.symbol === current));

  // Keep the highlighted row in view as the arrow keys move it.
  useEffect(() => {
    list.current?.querySelector(`[data-k="${active}"]`)?.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const pick = (symbol: string) => {
    addRecent(symbol);
    closeSearch();
    navigate(`/trade/${symbol}`);
  };
  const changeFilter = (f: Filter) => {
    setFilter(f);
    setSel(0);
  };
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSel(Math.min(active + 1, rows.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSel(Math.max(active - 1, 0));
    } else if (e.key === 'Enter' && rows[active]) {
      e.preventDefault();
      pick(rows[active]!.inst.symbol);
    } else if (e.key === 'Tab' && !narrow) {
      e.preventDefault();
      const k = FILTERS.findIndex((f) => f.id === filter);
      changeFilter(FILTERS[(k + (e.shiftKey ? FILTERS.length - 1 : 1)) % FILTERS.length]!.id);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closeSearch();
      anchor?.focus();
    }
  };

  return (
    <div ref={panel} role="dialog" aria-label="Choose a market" style={style} className="shadow-pop drop-in fixed z-50 flex flex-col rounded-xl border border-line bg-panel p-1.5 text-fg">
      <label className={cx('flex shrink-0 items-center gap-2 rounded-lg bg-raised text-muted', narrow ? 'h-11 px-3' : 'h-[38px] px-2.5')}>
        <Icon name="search" size={narrow ? 17 : 15} />
        <input
          ref={input}
          type="search"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setSel(0);
          }}
          onKeyDown={onKeyDown}
          placeholder={narrow ? 'Search markets' : 'Search symbol or name, e.g. gold, xau, nasdaq'}
          aria-label="Search markets"
          role="combobox"
          aria-expanded="true"
          aria-controls="market-search-list"
          aria-activedescendant={rows[active] ? `market-search-${active}` : undefined}
          autoComplete="off"
          spellCheck={false}
          className={cx('min-w-0 flex-1 bg-transparent text-fg outline-none placeholder:text-muted', narrow ? 'text-[16px]' : 'text-[14px]')}
        />
        {!narrow && <kbd className="num rounded border border-b-2 border-line-strong px-1.5 text-[10.5px] text-muted">Esc</kbd>}
      </label>

      <div role="group" aria-label="Asset class" className="no-scrollbar flex shrink-0 gap-1 overflow-x-auto px-0.5 pt-2 pb-1.5">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            aria-pressed={filter === f.id}
            onClick={() => {
              changeFilter(f.id);
              input.current?.focus();
            }}
            className={cx(
              'inline-flex shrink-0 items-center gap-1.5 rounded-full border font-medium whitespace-nowrap',
              narrow ? 'h-8 px-3 text-[13px]' : 'h-[26px] px-2.5 text-xs',
              filter === f.id ? 'border-line-strong bg-raised text-fg' : 'border-line text-muted hover:text-fg',
            )}
          >
            {f.label}
            {typed && <span className="num text-[10.5px] text-muted">{counts[f.id] ?? 0}</span>}
          </button>
        ))}
      </div>
      <div className="my-1 h-px shrink-0 bg-line" />

      <div ref={list} className="no-scrollbar min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {!typed && recentInst.length > 0 && (
          <>
            <SectionHead right={<button type="button" onClick={clearRecent} className="bg-transparent text-muted normal-case hover:text-fg">Clear</button>}>Recent</SectionHead>
            <div className="flex flex-wrap gap-1.5 px-2.5 pt-0.5 pb-2">
              {recentInst.map((i) => (
                <button key={i.symbol} type="button" onClick={() => pick(i.symbol)} className="inline-flex h-7 items-center gap-1.5 rounded-full border border-line bg-raised py-0 pr-2.5 pl-1 text-xs font-semibold text-fg">
                  <InstrumentIcon symbol={i.symbol} size={20} />
                  <PairName symbol={i.symbol} assetClass={i.assetClass} tag={false} />
                </button>
              ))}
            </div>
          </>
        )}
        <SectionHead>{typed ? (rows.length ? `${rows.length} result${rows.length === 1 ? '' : 's'}` : 'No results') : 'Trending · 24h volume'}</SectionHead>
        {rows.length > 0 && (
          <div aria-hidden="true" className={cx(COLS, 'px-2.5 pb-1 text-[10.5px] text-muted')}>
            <span />
            <span>Market</span>
            <span className="text-right max-sm:hidden">Lev</span>
            <span className="text-right">Price</span>
            <span className="text-right">24h</span>
          </div>
        )}
        <div id="market-search-list" role="listbox" aria-label={typed ? 'Results' : 'Trending markets'}>
          {rows.map((r, k) => (
            <ResultRow key={r.inst.symbol} row={r} k={k} active={k === active} current={r.inst.symbol === current} narrow={narrow} query={typed ? query : undefined} ticker={tickers[r.inst.symbol]} onPick={pick} onHover={setSel} />
          ))}
        </div>
        {typed && !rows.length && (
          <div className="px-6 py-8 text-center text-muted">
            No markets match “{query.trim()}”{filter !== 'ALL' && ` in ${FILTERS.find((f) => f.id === filter)!.label}`}.
            <div className="mt-1 text-xs">Try a name (gold), a market code (XAU) or an index (nasdaq).</div>
          </div>
        )}
      </div>

      <div className="mt-1.5 flex shrink-0 items-center justify-between gap-3 border-t border-line px-2.5 pt-2 pb-1 text-[11px] text-muted">
        {!narrow && (
          <span className="inline-flex flex-wrap gap-3">
            <Hint keys={['↑', '↓']}>move</Hint>
            <Hint keys={['Enter']}>open</Hint>
            <Hint keys={['Tab']}>asset class</Hint>
          </span>
        )}
        <Link to="/markets" className={cx('text-accent-fg no-underline hover:text-fg', narrow && 'mx-auto py-1.5 text-[13px]')}>
          All markets →
        </Link>
      </div>
    </div>
  );
}

function SectionHead({ children, right }: { children: React.ReactNode; right?: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between px-2.5 pt-2 pb-1 font-mono text-[10.5px] font-medium tracking-[0.07em] text-muted uppercase">
      <span>{children}</span>
      {right}
    </div>
  );
}

function Hint({ keys, children }: { keys: string[]; children: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      {keys.map((k) => (
        <kbd key={k} className="num rounded border border-b-2 border-line-strong px-1 text-[10px]">
          {k}
        </kbd>
      ))}
      {children}
    </span>
  );
}

function ResultRow({ row, k, active, current, narrow, query, ticker: t, onPick, onHover }: { row: Row; k: number; active: boolean; current: boolean; narrow: boolean; query?: string; ticker?: Ticker; onPick: (s: string) => void; onHover: (k: number) => void }) {
  const { inst: i, via } = row;
  const closed = t && !t.open;
  const neg = !!t?.chg24?.startsWith('-');
  return (
    <div
      id={`market-search-${k}`}
      data-k={k}
      role="option"
      aria-selected={active}
      aria-current={current || undefined}
      onClick={() => onPick(i.symbol)}
      onMouseMove={() => !active && onHover(k)}
      className={cx(COLS, 'cursor-pointer rounded-md px-2.5 whitespace-nowrap', narrow ? 'h-10 text-[13px]' : 'h-7 text-xs', active && 'bg-raised', current && 'shadow-[inset_2px_0_0_var(--accent)]')}
    >
      <InstrumentIcon symbol={i.symbol} size={16} />
      <span className="flex min-w-0 items-center gap-2 overflow-hidden">
        <PairName symbol={i.symbol} assetClass={i.assetClass} query={query} className="shrink-0 font-semibold" />
        <span className="min-w-0 truncate text-muted">
          <Marked text={i.name} query={query} />
          {i.assetClass === 'COMMODITY' && i.unit && !via ? ` · per ${i.unit}` : ''}
          {via && <span className="text-accent-fg"> · matches “{via}”</span>}
        </span>
        {closed && <span className="shrink-0 rounded border border-line-strong px-1 text-[9.5px] leading-[14px] font-medium text-muted">Closed</span>}
      </span>
      <span className="num text-right text-[11px] text-muted max-sm:hidden">{i.maxLeverage}x</span>
      <span className={cx('num text-right', closed && 'text-muted')}>{t ? fmtPrice(t.mark, i.priceDecimals, i.priceCcy) : '—'}</span>
      <span className={cx('num inline-flex items-center justify-end gap-0.5 text-right', !t ? 'text-muted' : neg ? 'text-down' : 'text-up')}>
        {t && <MoveArrow fall={neg} size={11} />}
        {t ? fmtPct(t.chg24) : '—'}
      </span>
    </div>
  );
}
